import { configureStore, isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';

import { logger } from '../utils/logger';

import { appReducer, appActions } from './slices/appSlice';
import { authReducer, authActions } from './slices/authSlice';
import { entitiesReducer, entitiesActions } from './slices/entitiesSlice';
import { matchingReducer, matchingActions } from './slices/matchingSlice';
import { notificationsReducer, notificationsActions } from './slices/notificationsSlice';
import { uiReducer, uiActions } from './slices/uiSlice';

const loggerMiddleware: Middleware = (store) => (next) => (action) => {
  const actionType =
    typeof action === 'object' && action !== null && 'type' in action
      ? (action as { type: string }).type
      : 'unknown';

  const payload =
    typeof action === 'object' && action !== null && 'payload' in action
      ? (action as { payload: unknown }).payload
      : undefined;

  const prevState = store.getState() as Record<string, unknown>;

  logger.info('REDUX', `Action: ${actionType}`, payload);

  const result = next(action);

  const nextState = store.getState() as Record<string, unknown>;

  // Find changed state slices
  const changedKeys = Object.keys(nextState).filter(
    (key) => prevState[key] !== nextState[key],
  );

  if (changedKeys.length > 0) {
    logger.info('REDUX', `State changed: [${changedKeys.join(', ')}]`);
  }

  if (isRejectedWithValue(action)) {
    logger.error('REDUX', `Thunk rejected: ${actionType}`, payload);
  }

  return result;
};

export const store = configureStore({
  reducer: {
    app: appReducer,
    auth: authReducer,
    entities: entitiesReducer,
    matching: matchingReducer,
    notifications: notificationsReducer,
    ui: uiReducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(loggerMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/**
 * Reset the entire store to initial state.
 * Used when Telegram Mini App is closed to ensure fresh state on next open.
 * Note: rulesAccepted is preserved (stored in localStorage).
 */
export function resetStore(): void {
  store.dispatch(appActions.resetAppState());
  store.dispatch(authActions.clearSession());
  store.dispatch(entitiesActions.resetEntities());
  store.dispatch(matchingActions.resetMatching());
  store.dispatch(notificationsActions.resetNotifications());
  store.dispatch(uiActions.resetUiState());
}
